import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Card, CardContent, Grid, Typography } from '@material-ui/core';
import withStyles from '@material-ui/styles/withStyles';
import { useTheme } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import { compose } from 'redux';

import { selectCompareFilesData } from 'features/wells/corrosion/helpers/corrosionlogs.selectors';

const CompareLogsBarChartContainer = ({ classes, compareFilesData }) => {
  const theme = useTheme();

  const { categories, series } = useMemo(() => {
    if (!compareFilesData?.length) {
      return { categories: [], series: [] };
    }

    const allJoints = compareFilesData.flatMap((f) =>
      f.corrosionLogFileData.map((r) => r.joint),
    );
    const categories = Array.from(new Set(allJoints)).sort((a, b) => a - b);

    const series = compareFilesData.map((file, idx) => {
      const date = new Date(file.logDate).toLocaleDateString();
      const name = `${file.fileName} (${date})`;
      const color = idx % 2 === 0 ? 'violet' : 'orange';

      const data = categories.map((joint) => {
        const row = file.corrosionLogFileData.find((r) => r.joint === joint);
        if (!row) return { y: 0, joint, file: name };

        return {
          y:
            row.maxPenetration != null
              ? parseFloat(Number(row.maxPenetration).toFixed(3))
              : null,
          joint: row.joint,
          file: name,
          depth:
            row.startDepth != null
              ? parseFloat(row.startDepth).toFixed(3)
              : null,
          class: row.class,
          position:
            row.maxMLPosition != null
              ? parseFloat(row.maxMLPosition).toFixed(3)
              : null,
          direction: row.direction,
          loss: row.totalMetalLoss,
          year: new Date(file.logDate).getFullYear(),
        };
      });

      return { name, color, data };
    });

    return { categories, series };
  }, [compareFilesData]);

  const chartHeight = categories.length * 40 + 80;

  const options = {
    chart: {
      type: 'bar',
      backgroundColor: theme.palette.background.paper,
      height: chartHeight,
    },
    title: {
      text: 'CORROSION LOG RESULTS',
      style: { color: theme.palette.common.white },
    },
    xAxis: {
      categories,
      title: {
        text: 'Joints',
        style: { color: theme.palette.common.white },
      },
      labels: { style: { color: theme.palette.common.white } },
    },
    yAxis: {
      min: 0,
      max: 100,
      title: {
        text: 'Maximum Corrosion Depth (%)',
        style: { color: theme.palette.common.white },
      },
      labels: {
        format: '{value}%',
        style: { color: theme.palette.common.white },
      },
      gridLineColor: theme.palette.background.default,
      opposite: true,
    },
    tooltip: {
      formatter: function () {
        return `
          <b>Joint:</b> ${this.point.joint}<br/>
          <b>Year:</b> ${this.point.year}<br/>
          <b>File:</b> ${this.point.file}<br/>
          <b>Depth:</b> ${this.point.depth} m<br/>
          <b>Max Depth:</b> ${this.point.y}%<br/>
          <b>Class:</b> ${this.point.class}<br/>
          <b>Position:</b> ${this.point.position}<br/>
          <b>Direction:</b> ${this.point.direction}<br/>
          <b>Total Metal Loss:</b> ${this.point.loss} %
        `;
      },
    },
    legend: {
      itemStyle: { color: theme.palette.common.white },
    },
    series,
    credits: { enabled: false },
  };

  return (
    <Grid container maxWidth={false} className={classes.cardContainer}>
      {compareFilesData.length === 0 && (
        <Grid item xs={12} className={classes.noData}>
          <Typography>No filters selected yet.</Typography>
        </Grid>
      )}
      {compareFilesData.length > 0 && (
        <Card className={classes.card}>
          <CardContent>
            <HighchartsReact
              highcharts={Highcharts}
              options={options}
              containerProps={classes.graphContainer}
            />
          </CardContent>
        </Card>
      )}
    </Grid>
  );
};

const styles = (theme) => ({
  card: {
    backgroundColor: theme.palette.grey.dark,
    width: '100%',
  },
  cardContainer: {
    padding: theme.spacing(2),
    maxHeight: '80vh',
    overflowY: 'auto',
  },
  graphContainer: {
    width: '100%',
  },
  noData: {
    display: 'flex',
    justifyContent: 'center',
  },
});

export default compose(
  connect(
    (state) => ({
      compareFilesData: selectCompareFilesData(state),
    }),
    {},
  ),
  withStyles(styles),
)(CompareLogsBarChartContainer);
